/**
 * Carrier assignment rules for orders.
 *
 * Rules:
 * 1. AT destination + manual-delivery items (Stecklinge, Sämlinge, Erden & Substrate, etc.) → 'postexpress'
 * 2. Customer requested 'goexpress' in checkout payload (STAA-94.4), allowed only for non-AT EU/DE → 'goexpress'
 * 3. Otherwise → 'gls' (default)
 *
 * Admins (Otmar) can change the carrier anytime in Payload admin.
 */

import { hasManualDeliveryItems } from './manualDeliveryDetector'
import { getShippingCountry } from './shippingCalculator'

export type OrderCarrier = 'gls' | 'postexpress' | 'goexpress' | 'post_ch' | 'other'

export interface DetermineCarrierParams {
  countryCode?: string | null
  items?: unknown[]
  requestedCarrier?: string | null
}

export function determineOrderCarrier(params: DetermineCarrierParams): OrderCarrier {
  const countryZone = getShippingCountry(params.countryCode ?? undefined)
  const isAT = countryZone === 'AT'
  const isCH =
    typeof params.countryCode === 'string' && params.countryCode.trim().toUpperCase() === 'CH'
  const isManualDelivery = hasManualDeliveryItems((params.items as never[]) || [])

  // Rule 1: AT + manual-delivery items → postexpress
  if (isAT && isManualDelivery) {
    return 'postexpress'
  }

  // Rule 2: Customer chose GoExpress (allowed only for non-AT EU/DE, not CH) → goexpress
  const isNonAtEuOrDe = (countryZone === 'DE' || countryZone === 'EU') && !isAT && !isCH
  const requested =
    typeof params.requestedCarrier === 'string' ? params.requestedCarrier.trim().toLowerCase() : ''
  if (requested === 'goexpress' && isNonAtEuOrDe) {
    return 'goexpress'
  }

  // Rule 3: Otherwise gls
  return 'gls'
}
